import React, {useCallback, useEffect, useState} from "react";
import classnames from "classnames";
import "./Header.scss";
import Settings from "./Settings/Settings"
import UserAccount from "./UserAccount/UserAccount";
import femslogo from "../../assets/logos/FEMS-LOGO.svg";
import { AuthProvider, getAccessTokenCookie, getTokens, setAccessTokenCookie, transformData } from '../Login/AuthProvider';
import TimeZone from '../Header/Timezone/TimeZone';
import { useLocation } from "react-router-dom";
import MobileMenu from "../MobileMenu/MobileMenu";
import { useStoreContext } from "src/store/context";
import { useAddAlert, useUser, useUserTokens } from "src/store/actions";
import Cookies from 'js-cookie';
import { refreshAuth, removeUser } from "../Login/RefreshAuth";
import { useIdleTimer } from 'react-idle-timer';
import moment from "moment-timezone";
import { removeByIndexesInPlace } from "src/store/common";

interface HeaderProps {
  basic?: boolean;
  megamenu?: boolean;
  extended?: boolean;
  firstName: string;
  lastName: string;
  children?: React.ReactNode;
  className?: string;
  color?:
    | "heritage"
    | "sweet-grass"
    | "big-sky"
    | "asphalt"
    | "maple"
    | "usda-blue"
    | "dandelion"
    | "cement"
    | "cloud-cover"
    | "light-grey"
    | "visited"
    | "hover"
    | "active"
    | "focus"
    | "soft-green"
    | "soft-yellow"
    | "soft-red"
    | "soft-blue";
}

const Header: React.FC<HeaderProps> = ({
  basic,
  megamenu,
  extended,
  children,
  firstName,
  lastName,
  className,
  color = "heritage",
  ...otherProps
}) => {
  const classes = classnames(
    "usa-header",
    {
      "usa-header--basic": basic,
      "usa-header--megamenu": megamenu,
      "usa-header--extended": extended,
    },
    className
  );

  const timeout = 1801000
  const promptBeforeIdle = 4_000
      
  const [state, dispatch] = useStoreContext();
  const setUser = useUser(dispatch);
  const addAlert = useAddAlert(dispatch);
  const setUserTokens = useUserTokens(dispatch);
  const [authToken, setAuthToken] = useState(getAccessTokenCookie('accessToken'))
  const location = useLocation();

  const refresh = useCallback(async() => {
    try{      
      const response:any = await refreshAuth(addAlert, setUser, setUserTokens);
      const userInfo =transformData(JSON.parse(response.jwt.napUserProfile));
      localStorage.setItem('user', JSON.stringify({exp: new Date(), userInfo}));
      localStorage.setItem('authExpires',moment().utc().add(Number(response.codeResponse.expires_in), 'seconds').format())
      setUser(userInfo);
      setUserTokens(getTokens(state.userTokens.authCode, response.codeResponse.access_token, response.codeResponse.refresh_token, response.codeResponse.expires_in));
      const expTime = (response.codeResponse.expires_in+200)/86400;
      setAccessTokenCookie(response.codeResponse.access_token, 'accessToken', expTime);
      setAccessTokenCookie(response.codeResponse.refresh_token, 'refreshToken',expTime);
      setAccessTokenCookie(state.userTokens.authCode, 'authCode',expTime);
      setAccessTokenCookie(response.codeResponse.expires_in, 'expiresIn',expTime);
    }
    catch(error){
      console.log('error', error)
      removeUser(setUser, setUserTokens)
    } 
  }, [addAlert, setUser, setUserTokens, state.userTokens]);

  const onIdle = () => {
    if(localStorage.getItem('user')){
      removeUser(setUser, setUserTokens);
      window.location.reload();
    }
  };
  const { getRemainingTime, } = useIdleTimer({
    onIdle,
    timeout,
    promptBeforeIdle,
    throttle: 1000
  })
/*if local storage user change*/
  useEffect(()=>{
    window.addEventListener('storage', () => {
      if(!localStorage.getItem('user')){
        Cookies.remove('accessToken');
        Cookies.remove('refreshToken');
        Cookies.remove('authCode');
        Cookies.remove('expiresIn');
        setUserTokens(null)
        setUser(null);
        window.location.reload();
      }
      else{
        const parsedSession = JSON.parse(localStorage.getItem('user')??'');
        setUser(parsedSession.userInfo);
        const auth = getAccessTokenCookie('authCode');
        const access = getAccessTokenCookie('accessToken');
        const refresh = getAccessTokenCookie('refreshToken');
        const expires = getAccessTokenCookie('expiresIn');
        setUserTokens(getTokens(auth, access, refresh, expires));
      }
    });
  }, [setUser, setUserTokens, location.pathname]);
  
  useEffect(() => {
    if(localStorage.getItem('user') && getAccessTokenCookie('refreshToken')){
      const interval = setInterval(() => {
        if(localStorage.getItem('authExpires')){
          const expireTime = localStorage.getItem('authExpires')
          if(moment(expireTime)<=moment()){
            refresh();
          }
        }
      }, 1000)
      return () => {
        if(interval){
          
          clearInterval(interval)

        }
      }
    }
  },[getRemainingTime, refresh])

  useEffect(()=>{
    if(localStorage.getItem('user') && getAccessTokenCookie('refreshToken')){
      const parsedSession = JSON.parse(localStorage.getItem('user')??'');
      setUser(parsedSession.userInfo);
      const auth = getAccessTokenCookie('authCode')
      const access = getAccessTokenCookie('accessToken');
      const refresh = getAccessTokenCookie('refreshToken');
      const expires = getAccessTokenCookie('expiresIn');
      setUserTokens(getTokens(auth, access, refresh, expires));
    }
    else{
      setUser(null);
      setUserTokens(null)
    }
  },[setUser, setUserTokens]);

  useEffect(()=> {
    if(getAccessTokenCookie('expiresIn') && localStorage.getItem('user')) {
      const ref = setInterval(() => {
          refresh();
      }, Number(getAccessTokenCookie('expiresIn')) * 1000);

      return () => {
        if(ref){
          clearInterval(ref);
        }
      }
    }
  }, [refresh]);

  /*if cookie removed then remove local storage user*/
  useEffect(()=>{
    const ref = setInterval(()=>{
      const currentCookie = getAccessTokenCookie('accessToken');
      if((authToken || localStorage.getItem('user')) && !currentCookie){
        removeUser(setUser, setUserTokens)
        setAuthToken(null);
        window.location.reload();

      }
      else if(currentCookie && currentCookie!==authToken){
        setAuthToken(currentCookie)
      }
    }, 1000);
    return () => {
      if(ref){
        clearInterval(ref);
      }
    }
  },[setUser, setUserTokens, authToken, setAuthToken])

  /* **********clean the browsers queue dl ids*/
    useEffect(()=>{
      const jobIdsStrings = localStorage.getItem('dlJobs');
      const newJobIdsArrayFromStorage = jobIdsStrings? JSON.parse(jobIdsStrings) : [];
      const dlTimeStrings = localStorage.getItem('dlReqTimes');
      const dlTimesArray = dlTimeStrings? JSON.parse(dlTimeStrings) : [];
      const currentDate = moment();
      let indicesToRemove: number[]=[];
      dlTimesArray.forEach((time:any, index:number)=>{
        const time1 = (moment(time).add(4, 'days'))
        if ((time1).isBefore(currentDate)) {
          indicesToRemove.push(index)
        }
      });      
      if(indicesToRemove?.length>=1){
        const newerJobIdArray = removeByIndexesInPlace(newJobIdsArrayFromStorage,indicesToRemove);
        const newerDlTimesArray = removeByIndexesInPlace(dlTimesArray, indicesToRemove)
        localStorage.setItem("dlJobs", JSON.stringify(newerJobIdArray));
        localStorage.setItem("dlReqTimes", JSON.stringify(newerDlTimesArray));
      }
    },[]);

  return (
    <header className={classes} {...otherProps} data-testid="Header">
      <div className="usa-navbar">
        <div className="usa-logo" title="FEMS Fire Environment Mapping System" role="img" aria-label="FEMS Fire Environment Mapping Systemgit">
          <h1 style={{display: 'none'}}>FEMS</h1>
          <img src={femslogo} alt="FEMS Fire Environment Mapping System" />
        </div>

        <div className ="hal-nav">
          <TimeZone/>
          <Settings/>
          {localStorage.getItem('user') && getAccessTokenCookie('refreshToken')?
            (
              <UserAccount 
                firstName={JSON.parse(localStorage.getItem('user') ??'{"userInfo":{"firstName": "John", "lastName":"Smith", "email":"New York", "appRoles": null}}').userInfo.firstName} 
                lastName={JSON.parse(localStorage.getItem('user') ??'{"userInfo":{"firstName": "John", "lastName":"Smith", "email":"New York","appRoles": null}}').userInfo.lastName} 
                email={JSON.parse(localStorage.getItem('user') ??'{"userInfo":{"firstName": "John", "lastName":"Smith", "email":"New York","appRoles": null}}').userInfo.email} 
                appRoles={JSON.parse(localStorage.getItem('user') ??'{"userInfo":{"firstName": "John", "lastName":"Smith", "email":"New York","appRoles": null}}').userInfo.appRoles}
              />
            )
            :(<AuthProvider/>)
          }
          <div className="MobileMenu"><MobileMenu/></div>
        </div>
      </div>
     
      <nav aria-label="Primary navigation" id="usda-nav-container" className={`usa-nav usda-nav ${color}`} data-testid="header-banner">
        {children}
      </nav>
    </header>
  );
};
export default Header;
